/**
 * One help article.
 *
 * SANITIZING THE BODY. `bodyHtml` is authored HTML from the sysadmin CMS, and
 * the rules only let a sysadmin write it — so this is not defending against a
 * hostile author so much as against the day that changes, a migration imports
 * something, or an article is pasted in from a source nobody checked.
 * `dangerouslySetInnerHTML` with unsanitized stored HTML is how an XSS gets
 * into a hospital application, and DOMPurify costs nothing to add now and is
 * awkward to retrofit after content exists.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BackLink } from '../../shared/BackLink';
import DOMPurify, { type Config } from 'dompurify';
import { useHelpArticles } from './useHelp';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import styles from '../../shared/Page.module.css';
import own from './Help.module.css';

/**
 * What an article is allowed to contain.
 *
 * Structural and textual elements only. No script, no style, no iframe, no
 * event handlers — an allowlist rather than a blocklist, because a blocklist
 * is a list of the attacks somebody thought of.
 */
const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'code', 'pre', 'blockquote',
    'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'table', 'thead', 'tbody',
    'tr', 'th', 'td', 'hr', 'span', 'div',
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
  // Only these schemes on a link. `javascript:` is a URL too.
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#|\/)/i,
};

/**
 * `back` is where the arrow and the "not found" action lead. The customer
 * application has a help center to go back to; the console has none, so a
 * console page that links to an article passes the page it came from.
 */
export function HelpArticlePage({ back = { to: '/help', label: 'All help articles' } }: { back?: { to: string; label: string } } = {}) {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useHelpArticles();

  const article = data?.find((a) => a.slug === slug);

  const safeBody = useMemo(
    () => (article?.bodyHtml ? DOMPurify.sanitize(article.bodyHtml, SANITIZE_CONFIG) : ''),
    [article],
  );

  if (isLoading) return <Spinner label="Loading article" />;

  if (error) {
    return (
      <PageState title="Could not load help" tone="alert" action={back}>
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  if (!article) {
    return (
      <PageState title="Article not found" action={back}>
        No help article has that address. It may have been renamed — the help
        center lists everything available.
      </PageState>
    );
  }

  return (
    <div className={own.wrap}>
      <div className={styles.titled}>
        <p className={own.eyebrow}>{article.category}</p>
        <div className={styles.titleLine}>
          <BackLink to={back.to} label={back.to === '/help' ? 'Back to all help articles' : back.label} />
          <h1 className={styles.title}>{article.title}</h1>
        </div>
        {article.summary && <p className={styles.subtitle}>{article.summary}</p>}
      </div>

      {article.processSteps.length > 0 && (
        <>
          <h2 className={styles.sectionTitle} style={{ marginTop: 'var(--sp-7)' }}>
            Step by step
          </h2>
          <ol className={own.steps}>
            {article.processSteps.map((step, i) => (
              <li key={i} className={own.stepItem}>
                {step}
              </li>
            ))}
          </ol>
        </>
      )}

      {safeBody && (
        <div
          className={own.body}
          // Sanitized directly above with an allowlist. See the module comment.
          dangerouslySetInnerHTML={{ __html: safeBody }}
        />
      )}

      {!safeBody && article.processSteps.length === 0 && (
        <p className={styles.absent}>This article has no content yet.</p>
      )}
    </div>
  );
}
